import type { FormInstance } from 'antd';
import Big from 'big.js';

import type { ExtranetHotelExperience } from 'app/typings/core/extranet/hotel/experiences';
import type { ExtranetHotelPack } from 'app/typings/core/extranet/hotel/packs';
import { rateToPercentage, roundPrice } from 'app/utils/pricing';

import {
  calculateStockNecessaryPerBooking,
  getPackScoreOnSingleDate,
} from '../../../utils/packs';

import {
  discountToPrice,
  getExperiencePrices,
} from './ExperienceDiscount/_components/experiencePricing';

export const calculatePackPricing = (
  pack: ExtranetHotelPack,
  formValues: Record<string, number>,
  form: FormInstance<{
    averageRoomPrice: number;
    defaultNightRoomDiscount: number;
  }>,
  averageRoomPrice: number,
  defaultNightRoomDiscount: number,
  getExperience: (experienceId?: number) => ExtranetHotelExperience | undefined,
  getOriginal: (experienceId?: number) => ExtranetHotelExperience | undefined
) => {
  const mergedValues = pack.packExperiences.reduce(
    (acc, pe) => {
      const key = `experienceDiscount-${pack.id}-${pe.experienceId}`;
      const fromForm = formValues[key] ?? form.getFieldValue(key);

      acc[key] = fromForm ?? rateToPercentage(pe.discount ?? 0);

      return acc;
    },
    {} as Record<string, number>
  );

  const { experiencesTotal, experiencesTotalOriginal } =
    pack.packExperiences.reduce(
      (acc, packExperience) => {
        if (!packExperience.isIncluded) {
          return acc;
        }

        const experienceDiscount =
          mergedValues[
            `experienceDiscount-${pack.id}-${packExperience.experienceId}`
          ];

        const experience =
          getExperience(packExperience.experienceId) ??
          getOriginal(packExperience.experienceId);
        const experiencePrices = getExperiencePrices(pack, experience);

        if (!experiencePrices) {
          return acc;
        }

        const quantity = calculateStockNecessaryPerBooking(packExperience);
        const { adultPrice, staycationAdultPrice } = experiencePrices;

        if (!adultPrice) {
          return acc;
        }

        const discountedAdultPrice =
          staycationAdultPrice ??
          discountToPrice(adultPrice, experienceDiscount);

        return {
          experiencesTotalOriginal: acc.experiencesTotalOriginal.plus(
            Big(adultPrice).times(quantity)
          ),
          experiencesTotal: acc.experiencesTotal.plus(
            Big(discountedAdultPrice).times(quantity)
          ),
        };
      },
      { experiencesTotal: Big(0), experiencesTotalOriginal: Big(0) }
    );

  const roomPrice = averageRoomPrice ?? 0;

  const originalPackPrice = roundPrice(
    experiencesTotalOriginal.plus(roomPrice)
  );
  const discountedPackPrice = roundPrice(
    experiencesTotal.plus(
      Big(roomPrice)
        .times(Big(1).minus(Big(defaultNightRoomDiscount).div(100)))
        .round(0)
    )
  );

  const discount =
    originalPackPrice > 0
      ? Big(originalPackPrice)
          .minus(discountedPackPrice)
          .div(originalPackPrice)
          .times(100)
          .round(1)
          .toNumber()
      : 0;

  return {
    score: getPackScoreOnSingleDate(discount),
    originalPackPrice,
    discountedPackPrice,
    discount,
  };
};
